/**
 * C2 · La matriz ruta × rol, en un único sitio (D51, D52, D59).
 *
 * Se indexa por el PATH DE CONFIGURACIÓN de la ruta, no por la URL: el guard lo
 * reconstruye con `pathFromRoot`, así que `/pacientes/123/citas` y
 * `/pacientes/999/datos` dan la misma clave `pacientes/:id/:panel`.
 *
 * Fail-closed: una clave que no esté aquí se deniega. Una ruta nueva que nadie
 * haya decidido nace cerrada, no abierta.
 *
 * La tabla esperada, escrita a mano, está en docs/specs/app-guard-rol-plan.md
 * §4.2. Si este fichero y aquella tabla divergen, falla el test 12a.
 */

/** Los siete roles de la API (cns-api, `User.php:14-26`). */
export type UserRole =
    | 'admin'
    | 'receptionist'
    | 'lab'
    | 'rx'
    | 'specialist'
    | 'regular'
    | 'client';

export const USER_ROLES: ReadonlyArray<UserRole> = [
    'admin',
    'receptionist',
    'lab',
    'rx',
    'specialist',
    'regular',
    'client',
];

/**
 * Las rutas guardadas que tiene todo el mundo, incluido quien no tiene rol
 * (D52). Una sola lista: la usan la matriz de abajo y el fallback de D52/D61,
 * para que no puedan divergir.
 */
export const COMMON_ROUTES: ReadonlyArray<string> = ['dashboard', 'perfil'];

/**
 * Qué rol entra en qué ruta. `admin` está en todas (D51).
 *
 * Las claves son exactamente las 27 rutas bajo el bloque de negocio. Las otras
 * cuatro URLs del inventario de C0 (`sign-in`, `sign-out`, `403` y `404`) viven
 * en bloques sin este guard y no aparecen aquí a propósito: quedan fuera por
 * estructura, no por matriz. El test 12g lo comprueba contra los
 * `loadChildren` de verdad.
 */
export const ROUTE_ROLES: Readonly<Record<string, ReadonlyArray<UserRole>>> = {
    // Comunes: los siete roles. Sin rol también entra, por COMMON_ROUTES.
    dashboard: USER_ROLES,
    perfil: USER_ROLES,

    // D59: las dos subrutas del dashboard.
    'dashboard/rayos-x': ['admin', 'rx'],
    'dashboard/recepcion': ['admin', 'receptionist'],

    agendas: ['admin', 'receptionist', 'rx'],

    convenios: ['admin', 'receptionist'],
    'convenios/:id': ['admin', 'receptionist'],
    'convenios/:id/:panel': ['admin', 'receptionist'],

    especialidades: ['admin', 'receptionist'],
    'especialidades/:id': ['admin', 'receptionist'],
    'especialidades/:id/:panel': ['admin', 'receptionist'],

    especialistas: ['admin', 'receptionist'],
    'especialistas/:id': ['admin', 'receptionist'],
    'especialistas/:id/:panel': ['admin', 'receptionist'],

    examenes: ['admin', 'lab'],

    // D59: ni el listado, ni `:id`, ni `:id/:panel` para `lab` ni para `rx`.
    pacientes: ['admin', 'receptionist'],
    'pacientes/:id': ['admin', 'receptionist'],
    'pacientes/:id/:panel': ['admin', 'receptionist'],

    'plantillas-reportes': ['admin', 'receptionist'],

    pruebas: ['admin', 'receptionist', 'lab'],
    'pruebas/:id': ['admin', 'receptionist', 'lab'],
    'pruebas/:id/:panel': ['admin', 'receptionist', 'lab'],

    'rayos-x': ['admin', 'rx'],

    'reportes-medicos': ['admin', 'receptionist'],

    usuarios: ['admin', 'receptionist'],
    'usuarios/:id': ['admin', 'receptionist'],
    'usuarios/:id/:panel': ['admin', 'receptionist'],
};

/**
 * Un valor del servidor solo es un rol si es uno de los siete. Cualquier otra
 * cosa (`''`, `null`, un rol nuevo que añada la API) se trata como «sin rol».
 */
export function isUserRole(value: unknown): value is UserRole {
    return (
        typeof value === 'string' &&
        (USER_ROLES as ReadonlyArray<string>).includes(value)
    );
}
